import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { CheckCircle2, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { BrandLogo } from "../shared/BrandLogo";
import { formatCurrency } from "../../lib/utils";

// --- Normalized receipt data builders -------------------------------------
// A receipt is per-PAYMENT. Build its data from either a Stripe PaymentLink
// (public /pay page) or a Payment row (portal + admin), so cash/manual
// payments without a link still get a receipt.
export function receiptFromLink(link = {}) {
  return {
    receiptNo: (link.token || "").slice(0, 8).toUpperCase(),
    paidAt: link.paid_at,
    customerName: link.customer_name,
    purposeLabel: link.purpose_label || "Subscription",
    subscriptionId: link.subscription_id,
    methodLabel: methodLabel(link.payment_method),
    cardBrand: link.card_brand,
    cardLast4: link.card_last4,
    subtotal: link.subtotal ?? link.amount,
    gstEnabled: link.gst_enabled,
    gstRate: link.gst_rate,
    gstAmount: link.gst_amount,
    total: link.total ?? link.amount,
  };
}

export function receiptFromPayment(payment = {}, bd = {}, customerName) {
  return {
    receiptNo: String(payment.transaction_no || payment.id || ""),
    paidAt: payment.paid_at || payment.date || payment.created_at,
    customerName: customerName || payment.customer_name,
    purposeLabel: payment.purpose_label || "Subscription",
    subscriptionId: payment.subscription_id,
    methodLabel: payment.payment_method_label || methodLabel(payment.payment_method),
    cardBrand: payment.card_brand,
    cardLast4: payment.card_last4,
    subtotal: bd.subtotal,
    gstEnabled: bd.enabled,
    gstRate: bd.rate,
    gstAmount: bd.gst,
    total: bd.total,
  };
}

function methodLabel(method) {
  const m = String(method ?? "");
  if (m === "1") return "Cash";
  if (m === "2") return "Online";
  return null;
}

function cardBrandLabel(brand) {
  if (!brand) return "Card";
  const map = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover", jcb: "JCB", diners: "Diners", unionpay: "UnionPay" };
  const key = String(brand).toLowerCase();
  return map[key] || (key.charAt(0).toUpperCase() + key.slice(1));
}

function formatPaidAt(value) {  if (value && /^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) {
    const [y, m, d] = String(value).trim().split("-");
    return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" })
      .format(new Date(Number(y), Number(m) - 1, Number(d)));
  }
  let parsed;
  if (!value) parsed = new Date();
  else if (/^\d+$/.test(String(value).trim())) {
    const n = Number(value);
    parsed = new Date(n < 1e12 ? n * 1000 : n);
  } else parsed = new Date(String(value).replace(/\//g, "-"));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(parsed);
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-text-muted">{label}</span>
      <span className="font-medium text-text-main" data-testid={testId}>{value}</span>
    </div>
  );
}

export function PaymentReceipt({ data = {}, footer = null }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const receiptNo = data.receiptNo || "";
  const total = data.total;

  const render = async () => toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });

  const save = async () => {
    setBusy("save");
    try {
      const dataUrl = await render();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `virsa-receipt-${receiptNo || "payment"}.png`;
      a.click();
      toast.success("Receipt saved");
    } catch (e) {
      toast.error("Could not save the receipt. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const share = async () => {
    setBusy("share");
    try {
      const dataUrl = await render();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `virsa-receipt-${receiptNo || "payment"}.png`, { type: "image/png" });
      const payload = {
        title: "The Virsa Tiffin — Payment Receipt",
        text: `Payment of ${formatCurrency(total)} received. Receipt #${receiptNo}.`,
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...payload, files: [file] });
      } else if (navigator.share) {
        await navigator.share(payload);
      } else {
        await save();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available on this device. We've saved it instead.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="payment-receipt">
      <div ref={cardRef} className="rounded-2xl bg-white p-6 text-left" data-testid="payment-receipt-card">
        <div className="flex items-center justify-between">
          <BrandLogo className="h-9" />
          <span className="inline-flex items-center gap-1 rounded-full bg-success-bg px-2.5 py-1 text-xs font-semibold text-success">
            <CheckCircle2 size={13} strokeWidth={2} /> Paid
          </span>
        </div>

        <div className="mt-5 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Amount Paid</p>
          <p className="mt-1 font-heading text-3xl font-extrabold text-primary" data-testid="payment-receipt-total">
            {formatCurrency(total)}
          </p>
          <p className="mt-1 text-xs text-text-muted">{formatPaidAt(data.paidAt)}</p>
        </div>

        <div className="mt-5 border-t border-dashed border-border pt-3">
          <Row label="Receipt No" value={`#${receiptNo}`} testId="payment-receipt-no" />
          <Row label="Billed to" value={data.customerName || "—"} testId="payment-receipt-name" />
          <Row label="For" value={data.purposeLabel || "Subscription"} />
          {data.subscriptionId != null && <Row label="Subscription" value={`#${data.subscriptionId}`} />}
          {data.methodLabel && <Row label="Method" value={data.methodLabel} testId="payment-receipt-method" />}
          {data.cardLast4 && (
            <Row label="Card" value={`${cardBrandLabel(data.cardBrand)} •••• ${data.cardLast4}`} testId="payment-receipt-card" />
          )}
        </div>

        <div className="mt-3 border-t border-dashed border-border pt-3">
          <Row label="Subtotal" value={formatCurrency(data.subtotal ?? total)} />
          {data.gstEnabled && (
            <Row label={`GST (${data.gstRate}%)`} value={`+ ${formatCurrency(data.gstAmount)}`} />
          )}
          <div className="mt-1 flex items-center justify-between border-t border-border pt-2">
            <span className="text-sm font-semibold text-text-main">Total Paid</span>
            <span className="font-heading text-lg font-bold text-primary">{formatCurrency(total)}</span>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-text-muted">Thank you for choosing The Virsa Tiffin.</p>
      </div>

      <div className="mt-4 flex gap-3">
        <Button variant="outline" className="flex-1" onClick={save} disabled={busy !== null} data-testid="payment-receipt-save-button">
          <Download size={16} strokeWidth={1.75} /> {busy === "save" ? "Saving…" : "Save"}
        </Button>
        <Button variant="outline" className="flex-1" onClick={share} disabled={busy !== null} data-testid="payment-receipt-share-button">
          <Share2 size={16} strokeWidth={1.75} /> {busy === "share" ? "Sharing…" : "Share"}
        </Button>
      </div>

      {footer}
    </div>
  );
}
